import { useCallback, useEffect, useState } from 'react'

import {
  getAllAncestorsForItem,
  getAllDescendantsForItem,
  getTreeItemState,
} from 'cng-common/components/treeSelect'
import { ITreeStructure } from 'cng-common/components/treeSelect'
import {
  IUseSelectedValuesParameters,
  TUseSelectedValuesResult,
} from 'cng-common/components/treeSelect/models/iUseSelectedValues'

export const useSelectedValues = ({
  values,
  flatData,
  typeOfSelectedNodes,
}: IUseSelectedValuesParameters): TUseSelectedValuesResult => {
  const [selectedItems, setSelectedItems] = useState<ITreeStructure[]>([])

  const areSelectedItemsEqual = useCallback((left: ITreeStructure[], right: ITreeStructure[]) => {
    if (left.length !== right.length) {
      return false
    }

    return left.every((leftItem, index) => {
      const rightItem = right[index]

      return (
        rightItem &&
        leftItem.itemId === rightItem.itemId &&
        leftItem.checkedState === rightItem.checkedState
      )
    })
  }, [])

  const updateSelectedItems = useCallback(
    (nextSelectedItems: ITreeStructure[]) => {
      setSelectedItems((prevSelectedItems) => {
        if (areSelectedItemsEqual(prevSelectedItems, nextSelectedItems)) {
          return prevSelectedItems
        }

        return nextSelectedItems
      })
    },
    [areSelectedItemsEqual]
  )

  const getUpdatedSelectedItems = useCallback(() => {
    const initialSelectedValues = flatData.filter((it) =>
      values.some((selectedId) => selectedId === it.itemId)
    )

    const withAncestors = initialSelectedValues.reduce<ITreeStructure[]>(
      (acc, item) => [...getAllAncestorsForItem(item), ...acc, item],
      []
    )

    const deduped = withAncestors.reduce((unique, current) => {
      return unique.some((item) => item.itemId === current.itemId) ? unique : [...unique, current]
    }, [] as ITreeStructure[])

    const valueSet = new Set(values)

    return deduped.map<ITreeStructure>((it) => ({
      ...it,
      checkedState: valueSet.has(it.itemId) ? 'full' : 'indeterminate',
    }))
  }, [values, flatData])

  const getDisplayedItemsByLeaves = useCallback(() => {
    const initialSelectedValues = flatData.filter((it) =>
      values.some((selectedId) => selectedId === it.itemId)
    )

    return initialSelectedValues
      .reduce<ITreeStructure[]>(
        (acc, item) => [...getAllAncestorsForItem(item), ...getAllDescendantsForItem(item), ...acc],
        []
      )
      .map<ITreeStructure>((it, i, origin) => ({
        ...it,
        checkedState: getTreeItemState(it.itemId, flatData, origin),
      }))
      .reduce((unique, current) => {
        return unique.some((item) => item.itemId === current.itemId) ? unique : [...unique, current]
      }, [] as ITreeStructure[])
  }, [values, flatData])

  useEffect(() => {
    if (typeOfSelectedNodes === 'onlyLeavesItems') {
      updateSelectedItems(getDisplayedItemsByLeaves())
      return
    }

    updateSelectedItems(getUpdatedSelectedItems())
  }, [getUpdatedSelectedItems, getDisplayedItemsByLeaves, typeOfSelectedNodes, updateSelectedItems])

  return [selectedItems, setSelectedItems]
}
